import { FC, Fragment, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import apiClient from "../utils/apiClient";
import Provider from "../enum/Provider.enum";
import NumberTextField from "./NumberTextFeild";
import { AxiosError } from "axios";
import MotivationSchemeEnum from "../enum/MotivationScheme.enum";
import { useSelector } from "react-redux";

const FieldRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-bottom: 20px;
`;

const FieldColumn = styled.div`
  flex: 1;
  min-width: 200px;
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const mtOptions = {
  broker: "broker",
  retention: "retention",
  attraction: "attraction",
};

type AgentManagerShereType = {
  a: number;
  M: number;
};

type DetailsType = {
  [K in keyof typeof mtOptions]: AgentManagerShereType;
};

type DetailsReferralType = {
  referral: {
    reward: number;
    limit: number;
    currency: string;
    days_from_opening: number;
  };
};

type MotivationScheme =
  | {
      id: number;
      prefix: string;
      description: string;
      details: DetailsType;
      is_hidden: boolean;
      is_used?: boolean;
      type: MotivationSchemeEnum.DEFAULT;
    }
  | {
      id: number;
      prefix: string;
      description: string;
      details: DetailsReferralType;
      is_hidden: boolean;
      is_used?: boolean;
      type: MotivationSchemeEnum.REFERRAL;
    };

type MotivationSchemePopupProps = {
  open: boolean;
  scheme: MotivationScheme | null;
  onClose: () => void;
  onSaved: () => void;
};

type MotivationPartsType = {
  motivation_type: keyof typeof mtOptions | "referral";
  recipient: "a" | "M" | "reward" | "limit" | "days_from_opening" | "currency";
  percent: number | string;
};

function isReferral(
  scheme: MotivationScheme,
): scheme is Extract<
  MotivationScheme,
  { type: MotivationSchemeEnum.REFERRAL }
> {
  return (
    scheme.type === MotivationSchemeEnum.REFERRAL ||
    "referral" in scheme.details
  );
}

const MotivationSchemePopup: FC<MotivationSchemePopupProps> = ({
  open,
  onClose,
  scheme,
  onSaved,
}) => {
  const { t } = useTranslation();

  const isEdit = Boolean(scheme && scheme.id);
  const provider = useSelector((state: any) => state.app.provider);

  const [description, setDescription] = useState<string>("");
  const [isHidden, setIsHidden] = useState<boolean>(false);
  const [details, setDetails] = useState<DetailsType | null>(null);
  const [detailsReferral, setDetailsReferral] =
    useState<DetailsReferralType | null>(null);
  const [type, setType] = useState<MotivationSchemeEnum>(
    MotivationSchemeEnum.DEFAULT,
  );

  useEffect(() => {
    if (scheme) {
      const schemeType = isReferral(scheme)
        ? MotivationSchemeEnum.REFERRAL
        : MotivationSchemeEnum.DEFAULT;
      setType(schemeType);
      setDescription(scheme.description);
      setIsHidden(scheme.is_hidden);

      if (schemeType === MotivationSchemeEnum.DEFAULT) {
        const parsedDetails = Object.fromEntries(
          Object.entries(scheme.details).map(([key, value]) => [
            key,
            Object.fromEntries(
              Object.entries(value).map(([k, v]) => [k, Number(v)]),
            ),
          ]),
        ) as DetailsType;

        setDetails(parsedDetails);
      } else {
        const rawDetails = scheme.details as DetailsReferralType;
        const parsedDetails: DetailsReferralType = {
          referral: {
            reward: Number(rawDetails?.referral?.reward),
            limit: Number(rawDetails?.referral?.limit),
            currency: rawDetails?.referral?.currency,
            days_from_opening: Number(rawDetails?.referral?.days_from_opening),
          },
        };

        setDetailsReferral(parsedDetails);
      }
    } else {
      setDescription("");
      setIsHidden(false);
      changeType(MotivationSchemeEnum.DEFAULT);
    }
  }, [scheme]);

  const update = (
    mt: keyof DetailsType,
    key: keyof AgentManagerShereType,
    val: number,
  ) => {
    setDetails((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        [mt]: {
          ...prev[mt],
          [key]: val,
        },
      };
    });
  };

  const hasInvalidRow = details
    ? Object.values(details).some(
        (entry) => (entry.a || 0) + (entry.M || 0) > 20,
      )
    : false;

  const isSaveDisabled =
    (hasInvalidRow &&
      provider === Provider.BELARUS &&
      type === MotivationSchemeEnum.DEFAULT) ||
    !!scheme?.is_used;

  const handleSave = async () => {
    const parts: MotivationPartsType[] = [];

    switch (type) {
      case MotivationSchemeEnum.DEFAULT:
        (Object.keys(mtOptions) as Array<keyof typeof mtOptions>).forEach(
          (mt) => {
            if (!details) return;
            parts.push({
              motivation_type: mt,
              recipient: "a",
              percent: details[mt].a,
            });
            parts.push({
              motivation_type: mt,
              recipient: "M",
              percent: details[mt].M,
            });
          },
        );
        break;

      case MotivationSchemeEnum.REFERRAL:
        parts.push({
          motivation_type: "referral",
          recipient: "reward",
          percent: detailsReferral?.referral?.reward ?? 0,
        });
        parts.push({
          motivation_type: "referral",
          recipient: "limit",
          percent: detailsReferral?.referral?.limit ?? 0,
        });
        parts.push({
          motivation_type: "referral",
          recipient: "currency",
          percent: detailsReferral?.referral?.currency ?? "USD",
        });
        parts.push({
          motivation_type: "referral",
          recipient: "days_from_opening",
          percent: detailsReferral?.referral?.days_from_opening ?? 0,
        });
        break;
    }

    try {
      await apiClient.post("/data/SaveMotivationScheme", {
        id: scheme?.id || 0,
        type,
        description,
        is_hidden: isHidden,
        parts,
      });

      onSaved();
    } catch (e: any | AxiosError) {
      alert(e?.response?.data?.error ?? t("motivationSchemePopup.saveError"));
    }
  };

  const changeType = (newType: MotivationSchemeEnum) => {
    setType(newType);

    switch (newType) {
      case MotivationSchemeEnum.DEFAULT:
        setDetails({
          attraction: { a: 0, M: 0 },
          broker: { a: 0, M: 0 },
          retention: { a: 0, M: 0 },
        });
        break;

      case MotivationSchemeEnum.REFERRAL:
        setDetailsReferral({
          referral: {
            reward: 0,
            limit: 0,
            currency: "USD",
            days_from_opening: 0,
          },
        });
        break;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit
          ? t("motivationSchemePopup.editTitle")
          : t("motivationSchemePopup.createTitle")}
      </DialogTitle>

      <DialogContent dividers>
        <Section>
          <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
            {t("motivationSchemePopup.main")}
          </Typography>

          <FieldRow>
            <FieldColumn>
              <FormControl fullWidth size="small">
                <InputLabel id="scheme-type-label">
                  {t("motivationSchemePopup.type")}
                </InputLabel>

                <Select
                  size="small"
                  value={type}
                  label={t("motivationSchemePopup.type")}
                  onChange={(e) =>
                    changeType(e.target.value as MotivationSchemeEnum)
                  }
                  variant="outlined"
                  disabled={!!scheme?.id}
                  labelId="scheme-type-label"
                >
                  <MenuItem value={MotivationSchemeEnum.DEFAULT}>
                    {t("motivationSchemePopup.defaultType")}
                  </MenuItem>

                  {provider === Provider.BELARUS && (
                    <MenuItem value={MotivationSchemeEnum.REFERRAL}>
                      {t("motivationSchemePopup.referralType")}
                    </MenuItem>
                  )}
                </Select>
              </FormControl>
            </FieldColumn>
          </FieldRow>

          <FieldRow>
            <FieldColumn>
              <TextField
                size="small"
                label={t("motivationSchemePopup.description")}
                placeholder={t("motivationSchemePopup.descriptionPlaceholder")}
                value={description}
                fullWidth
                onChange={(e) => setDescription(e.target.value)}
              />
            </FieldColumn>
          </FieldRow>
        </Section>

        {type === MotivationSchemeEnum.DEFAULT && (
          <Section>
            <Typography variant="h6" component="h2">
              {t("motivationSchemePopup.percentRates")}
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={4}></Grid>

              <Grid size={4}>
                <Typography fontWeight={500} align="center">
                  {t("motivationSchemePopup.agent")}
                </Typography>
              </Grid>

              <Grid size={4}>
                <Typography fontWeight={500} align="center">
                  {t("motivationSchemePopup.manager")}
                </Typography>
              </Grid>

              {Object.entries(mtOptions).map(([mt]) => {
                const key = mt as keyof typeof mtOptions;
                const label = t(`motivationSchemePopup.mtOptions.${key}`);

                return (
                  <Fragment key={key}>
                    <Grid size={4}>
                      <Typography fontWeight={500}>{label}</Typography>
                    </Grid>

                    <Grid size={4}>
                      <NumberTextField
                        fullWidth
                        size="small"
                        value={details?.[key]?.a ?? 0}
                        roundTo={2}
                        maxValue={100}
                        minValue={0}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          update(key, "a", value);
                        }}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid>

                    <Grid size={4}>
                      <NumberTextField
                        fullWidth
                        size="small"
                        value={details?.[key]?.M ?? 0}
                        roundTo={2}
                        maxValue={100}
                        minValue={0}
                        onChange={(e) => {
                          const value = parseFloat(e.target.value) || 0;
                          update(key, "M", value);
                        }}
                        slotProps={{
                          input: {
                            endAdornment: (
                              <InputAdornment position="end">%</InputAdornment>
                            ),
                          },
                        }}
                      />
                    </Grid>

                    {provider === Provider.BELARUS &&
                      (details?.[key]?.M ?? 0) + (details?.[key]?.a ?? 0) >
                        20 && (
                        <Grid size={8} offset={4}>
                          <Alert severity="error">
                            {t("motivationSchemePopup.discountLimit20", {
                              label,
                            })}
                          </Alert>
                        </Grid>
                      )}

                    {provider !== Provider.BELARUS &&
                      (details?.[key]?.M ?? 0) + (details?.[key]?.a ?? 0) >
                        100 && (
                        <Grid size={8} offset={4}>
                          <Alert severity="error">
                            {t("motivationSchemePopup.discountLimit100", {
                              label,
                            })}
                          </Alert>
                        </Grid>
                      )}
                  </Fragment>
                );
              })}
            </Grid>
          </Section>
        )}

        {type === MotivationSchemeEnum.REFERRAL && (
          <Section>
            <Typography variant="h6" component="h2" sx={{ mb: 2 }}>
              {t("motivationSchemePopup.reward")}
            </Typography>

            <Stack spacing={2}>
              <NumberTextField
                label={t("motivationSchemePopup.rewardAmount")}
                fullWidth
                size="small"
                value={detailsReferral?.referral?.reward ?? 0}
                minValue={0}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;

                  setDetailsReferral((prev) => {
                    if (!prev) return prev;

                    return {
                      ...prev,
                      referral: {
                        ...prev.referral,
                        reward: value,
                      },
                    };
                  });
                }}
              />

              <NumberTextField
                label={t("motivationSchemePopup.depositAmount")}
                fullWidth
                size="small"
                value={detailsReferral?.referral?.limit ?? 0}
                minValue={0}
                onChange={(e) => {
                  const value = parseFloat(e.target.value) || 0;

                  setDetailsReferral((prev) => {
                    if (!prev) return prev;

                    return {
                      ...prev,
                      referral: {
                        ...prev.referral,
                        limit: value,
                      },
                    };
                  });
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <Select
                          value={detailsReferral?.referral?.currency ?? ""}
                          onChange={(e) =>
                            setDetailsReferral((prev) => {
                              if (!prev) return prev;

                              return {
                                ...prev,
                                referral: {
                                  ...prev.referral,
                                  currency: e.target.value,
                                },
                              };
                            })
                          }
                          variant="standard"
                          disableUnderline
                        >
                          <MenuItem value="RUB">RUB</MenuItem>
                          <MenuItem value="USD">USD</MenuItem>
                          <MenuItem value="BYN">BYN</MenuItem>
                        </Select>
                      </InputAdornment>
                    ),
                  },
                }}
              />

              <NumberTextField
                label={t("motivationSchemePopup.term")}
                fullWidth
                size="small"
                value={detailsReferral?.referral?.days_from_opening ?? 0}
                minValue={0}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 0;

                  setDetailsReferral((prev) => {
                    if (!prev) return prev;

                    return {
                      ...prev,
                      referral: {
                        ...prev.referral,
                        days_from_opening: value,
                      },
                    };
                  });
                }}
              />
            </Stack>
          </Section>
        )}
      </DialogContent>

      <DialogActions>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={isSaveDisabled}
          fullWidth
        >
          {isEdit
            ? t("motivationSchemePopup.save")
            : t("motivationSchemePopup.create")}
        </Button>

        <Button
          onClick={onClose}
          variant="outlined"
          color="secondary"
          fullWidth
        >
          {t("motivationSchemePopup.cancel")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MotivationSchemePopup;
